import { Switch } from '@headlessui/react';
import { MapIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon, MapPinIcon } from '@heroicons/react/24/solid';
import type L from 'leaflet';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MapContainer, Marker, Polygon, useMap, useMapEvents,
} from 'react-leaflet';

import classNames from '../../../../../utils/classNames';
import { isPointInPolygon, rgpdBlueDarkIcon } from '../../../../../utils/mapUtils';
import OverzoomTileLayer from '../../../../common/OverzoomTileLayer';
import type { Parcelle, SelectedCopro } from '../../types';
import type { AutocompleteFeature } from '../BanSearch';
import BanSearch from '../BanSearch';

interface QualificationMapProps {
  selectedCopro: SelectedCopro | null;
  parcelles: Parcelle[];
  mapCenter: [number, number];
  mapZoom: number;
  addressFound: boolean;
  onMapClick: (lat: number, lng: number) => void;
  onParcelleClick: (parcelleId: string, event: L.LeafletMouseEvent) => void;
  onSearchSelect: (result: AutocompleteFeature) => void;
  onUseInSearch: (address: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  shouldUpdateView: boolean;
  setShouldUpdateView: (value: boolean) => void;
  mapRef: React.MutableRefObject<L.Map | null>;
}

function MapComponent({
  mapCenter,
  mapZoom,
  shouldUpdateView,
  setShouldUpdateView,
  parcelles,
  onMapClick,
  mapRef,
}: {
  mapCenter: [number, number];
  mapZoom: number;
  shouldUpdateView: boolean;
  setShouldUpdateView: (value: boolean) => void;
  parcelles: Parcelle[];
  onMapClick: (lat: number, lng: number) => void;
  mapRef: React.MutableRefObject<L.Map | null>;
}) {
  const map = useMap();
  mapRef.current = map;

  useEffect(() => {
    if (!shouldUpdateView) { return; }
    map.setView(mapCenter, mapZoom);
    setShouldUpdateView(false);
  }, [mapCenter, mapZoom, shouldUpdateView, setShouldUpdateView, map]);

  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      const clickedInParcelle = parcelles.some(
        (parcelle) => isPointInPolygon([lng, lat], parcelle.geometry.coordinates[0][0]),
      );
      if (!clickedInParcelle) {
        onMapClick(lat, lng);
      }
    },
  });

  return null;
}

const QualificationMap: React.FC<QualificationMapProps> = ({
  selectedCopro,
  parcelles,
  mapCenter,
  mapZoom,
  addressFound,
  onMapClick,
  onParcelleClick,
  onSearchSelect,
  onUseInSearch,
  searchQuery,
  setSearchQuery,
  shouldUpdateView,
  setShouldUpdateView,
  mapRef,
}) => {
  const [isCadastre, setIsCadastre] = useState(false);
  const coproRawAddress = selectedCopro?.details?.adresseDeReference || '';

  const openGoogleMaps = useCallback(() => {
    const encodedAddress = encodeURIComponent(coproRawAddress);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank', 'noopener,noreferrer');
  }, [coproRawAddress]);

  // Overlay when no copro is selected
  if (!selectedCopro) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-100">
        <MapPinIcon className="mb-4 h-16 w-16 text-gray-400" />
        <p className="px-4 text-center text-lg font-medium text-gray-600">
          Veuillez sélectionner une copro à qualifier
        </p>
        <p className="mt-2 px-4 text-center text-sm text-gray-500">
          Cliquez sur "Valider la localisation" dans la liste à gauche
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Copro info */}
      <div className="mb-2 flex-shrink-0">
        <div className="text-sm text-gray-600">
          <b><u>Copropriété sélectionnée :</u></b>
          {' '}
          {selectedCopro.name}
        </div>
        <div className="text-sm text-gray-600">
          Adresse brute :
          {' '}
          {coproRawAddress || 'Non renseignée'}
          {coproRawAddress && (
            <button
              className="ml-2 inline rounded bg-gray-400 px-2 py-1 text-xs text-white"
              onClick={() => onUseInSearch(coproRawAddress)}
            >
              Utiliser dans la recherche
            </button>
          )}
        </div>
      </div>

      {/* Search and tools */}
      <div className="mb-2 flex-shrink-0">
        <BanSearch
          onSelectResult={onSearchSelect}
          searchQuery={searchQuery}
          resetSearchQuery={setSearchQuery}
        />
        <div className="mt-1 flex flex-row justify-between gap-2 text-xs">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="mr-1 inline-block h-4 w-4 text-rgpd-blue-dark" />
            En cas de doutes :
          </div>
          <div className="flex flex-row gap-1">
            <button
              onClick={openGoogleMaps}
              disabled={!addressFound}
              className={classNames(
                'bg-rgpd-blue-dark text-white py-1 px-2 rounded text-xs',
                !addressFound && 'opacity-50 cursor-not-allowed',
              )}
            >
              <MapIcon className="mr-1 inline-block h-4 w-4 text-white" />
              Maps
            </button>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="relative min-h-0 flex-1">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          maxZoom={21}
          attributionControl={false}
        >
          <MapComponent
            mapCenter={mapCenter}
            mapZoom={mapZoom}
            shouldUpdateView={shouldUpdateView}
            setShouldUpdateView={setShouldUpdateView}
            parcelles={parcelles}
            onMapClick={onMapClick}
            mapRef={mapRef}
          />
          <OverzoomTileLayer isCadastre={isCadastre} />
          {parcelles.map((parcelle) => (
            <Polygon
              key={parcelle.id}
              positions={parcelle.geometry.coordinates[0].map((ring) => ring.map((coord) => [coord[1], coord[0]] as [number, number]))}
              pathOptions={{
                color: 'blue',
                fillColor: 'blue',
                fillOpacity: 0.3,
              }}
              eventHandlers={{
                click: (e) => onParcelleClick(parcelle.id, e),
              }}
            />
          ))}
          {addressFound && <Marker position={mapCenter} icon={rgpdBlueDarkIcon('#FBBF24')} />}
        </MapContainer>
        <div className="absolute top-2 right-2 z-[600]">
          <div className="flex items-center space-x-2 rounded-xl bg-white p-1 shadow-md">
            <Switch
              checked={isCadastre}
              onChange={() => setIsCadastre(!isCadastre)}
              className={`${
                isCadastre ? 'bg-rgpd-blue-dark' : 'bg-gray-200'
              } relative inline-flex h-5 w-8 items-center rounded-full transition-colors`}
            >
              <span
                className={`${
                  isCadastre ? 'translate-x-4' : 'translate-x-1'
                } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
            <span className="text-xs font-medium text-gray-900">Cadastre</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualificationMap;
